import React, { FC, useEffect, useRef, useState } from 'react';
import { Button, Col, Container, Row, Collapse, Form as bootstrapForm } from 'react-bootstrap';
import { RouteComponentProps, useHistory } from 'react-router';
import { cloneDeep, forEach } from 'lodash';
import useForm from '../../hooks/api/form/useForm';
import useReports from '../../hooks/api/report/useReports';
import useReport from '../../hooks/api/report/useReport';
import _ from 'lodash';
import CheckboxInput from '../../components/library/inputs/checkbox/CheckboxInput';
import { Report, ReportContent, ReportField, ReportStep } from '../../services/report/ReportService';
import { v4 as uuidv4 } from 'uuid';
import { FieldModel } from '../../models/form/field';
import { FormattedMessage, useIntl } from 'react-intl';
import PageTitle from '../../components/layout/pageTitle/PageTitle';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { noop } from '../../utilities/FunctionUtilities';
import { addSuccessToast } from '../../components/errors/toast/ToastUtils';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { checkTranslation } from '../../utilities/FunctionUtilities';
import LoadingSpinner from '../../components/layout/loadingSpinner/LoadingSpinner';
import { getSecuritySection, SecuritySections, SecurityModel } from '../../models/security/security';
import { SectionModel } from '../../models/form/section';
import { StepModel } from '../../models/form/step';
import { FieldType } from '../../models/form/fieldType';
import { getText } from '../../utilities/CultureUtilities';
import { FallbackPage } from '../../components/layout/navbar/Navbar';

interface MatchParams {
  formId: string;
  reportId: string;
}

interface FieldReport {
  field: FieldModel;
  checked: boolean;
}

interface Values {
  reportName: string;
}

type Props = RouteComponentProps<MatchParams>;

const ReportsPage: FC<Props> = ({
  match: {
    params: { formId, reportId },
  },
}: Props) => {
  const [fields, setFields] = useState({});
  const [formSteps, setFormSteps] = useState<StepModel[]>([]);
  const [reportName, setReportName] = useState<string>('');
  const [reportSecurity, setReportSecurity] = useState<SecurityModel | null>(null);
  const [open, setOpen] = useState<string[]>([]);
  const organizationId = useSelector((state: RootState) => state.organization.organization?.id) as string;
  const { form, setFormId, isLoading, setOrganizationId: setId } = useForm(organizationId);
  const { createReport, updateReport } = useReports();
  const { report, setFormId: setReportFormId, setOrganizationId, setReportId, generateReport } = useReport();
  const security = useSelector((state: RootState) => state.organization.organization?.security);
  const reportRef = useRef<FormikProps<Values>>(null);
  const intl = useIntl();
  const history = useHistory();

  let generalFields: Array<ReportField> = [];

  const paymentFields: Array<FieldModel | any> = [
    {
      id: 'TotalPaid',
      text: intl.formatMessage({ id: 'reports.formTotalPaid' }),
    },
    {
      id: 'Discount',
      text: intl.formatMessage({ id: 'reports.discount' }),
    },
    {
      id: 'DiscountCode',
      text: intl.formatMessage({ id: 'reports.discountCode' }),
    },
    {
      id: 'PaymentMethod',
      text: intl.formatMessage({ id: 'reports.paymentMethod' }),
    },
    {
      id: 'PaymentMode',
      text: intl.formatMessage({ id: 'reports.paymentMode' }),
    },
    {
      id: 'PaymentStatus',
      text: intl.formatMessage({ id: 'reports.paymentStatus' }),
    },
  ];

  const administrationFields: Array<FieldModel | any> = [
    {
      enabled: true,
      excludeFromReport: false,
      hiddenText: false,
      id: 'SubmissionId',
      minLength: 0,
      maxLength: 50,
      required: false,
      size: 6,
      text: intl.formatMessage({ id: 'reports.submissionId' }),
      type: FieldType.Text,
      visible: true,
    },
    {
      id: 'SubmissionDate',
      text: intl.formatMessage({ id: 'reports.submissionDate' }),
    },
  ];

  const paymentSection: SectionModel[] = [
    {
      id: 'paymentSection',
      name: intl.formatMessage({ id: 'reports.paymentsSectionName' }),
      multiple: false,
      visible: true,
      fields: paymentFields,
    },
  ];

  const administrationSection: SectionModel[] = [
    {
      id: 'adminSection',
      name: intl.formatMessage({ id: 'reports.adminSectionName' }),
      multiple: false,
      visible: true,
      fields: administrationFields,
    },
  ];

  const generalStep: StepModel = {
    id: 'generalStep',
    name: intl.formatMessage({ id: 'reports.adminStepName' }),
    sections: administrationSection.concat(paymentSection),
  };

  useEffect(() => {
    if (organizationId) {
      setOrganizationId(organizationId);
      setId(organizationId);
    }
    if (formId) {
      setFormId(formId);
      setReportFormId(formId);
    }
  }, [formId, organizationId]);

  useEffect(() => {
    if (reportId && reportId != 'create') {
      setReportId(reportId);
    }
  }, [reportId]);

  useEffect(() => {
    if (organizationId) {
      setOrganizationId(organizationId);
    }
  }, [organizationId]);

  useEffect(() => {
    if (security) {
      setReportSecurity(getSecuritySection(security, SecuritySections.REPORT));
    }
    if (reportSecurity && !reportSecurity?.read) {
      history.push(FallbackPage);
    }
  }, [security, reportSecurity]);

  useEffect(() => {
    if (form) {
      if (reportId == 'create' || report) {
        setReportName(report?.content.name || '');

        const tempFields: { [key: string]: { [key: string]: FieldReport[] } } = {};
        const tempSteps = form.content.steps.concat(generalStep);
        setFormSteps(tempSteps);

        const generalReportStep: ReportStep = {
          id: 'generalStep',
          fields: report?.content.general as ReportField[],
        };
        const tempReportSteps = report?.content.steps.concat(generalReportStep);

        _.forEach(tempSteps, (step) => {
          const reportStep = _.find(tempReportSteps, { id: step.id });
          tempFields[step.id] = {};

          _.forEach(step.sections, (section) => {
            let sectionFields = [...section.fields];
            tempFields[step.id][section.id] = [];

            _.forEach(reportStep?.fields, (field) => {
              const formField = _.find(sectionFields, { id: field.id });

              if (formField) {
                tempFields[step.id][section.id].push({ field: formField, checked: true });
                sectionFields = _.filter(sectionFields, (stepField) => stepField.id != field.id);
              }
            });

            tempFields[step.id][section.id] = tempFields[step.id][section.id].concat(
              _.map(sectionFields, (stepField) => ({ field: stepField, checked: reportId == 'create' })),
            );
          });
          setFields(tempFields);
        });
      }
    }
  }, [report, form]);

  useEffect(() => {
    if (formSteps) {
      const sectionList: string[] = [];
      _.forEach(formSteps, (step) => {
        _.forEach(step.sections, (section) => {
          sectionList.push(section.id);
        });
      });
      setOpen(sectionList);
    }
  }, [formSteps]);

  const handleGoUp = (field: string, idx: number, section: string) => {
    if (idx > 0) {
      const tempFields = { ...fields };
      const tempObj = tempFields[field][section][idx - 1];
      tempFields[field][section][idx - 1] = tempFields[field][section][idx];
      tempFields[field][section][idx] = tempObj;
      setFields(tempFields);
    }
  };
  const handleGoDown = (field: string, idx: number, section: string) => {
    if (idx < fields[field][section].length - 1) {
      const tempFields = { ...fields };
      const tempObj = tempFields[field][section][idx + 1];
      tempFields[field][section][idx + 1] = tempFields[field][section][idx];
      tempFields[field][section][idx] = tempObj;
      setFields(tempFields);
    }
  };

  const handleCheckField = (field, step, section) => {
    const tempFields = { ...fields };
    const clickedField = _.find(tempFields[step][section], (f) => {
      return f.field.id == field.field.id;
    });
    clickedField.checked = !clickedField.checked;
    setFields(tempFields);
  };

  const handleCheck = (check: boolean) => {
    const fieldCopy = { ...fields };
    _.forEach(fieldCopy, (step) => {
      _.forEach(step, (section: FieldReport[]) => {
        _.forEach(section, (field: FieldReport) => {
          field.checked = check ? true : false;
        });
      });
    });
    setFields(fieldCopy);
  };

  const handleSaveReport = async (steps) => {
    const reportSteps: ReportStep[] = [];
    const tempSteps = cloneDeep(steps);
    const { generalStep, ...otherSteps } = tempSteps;

    forEach(otherSteps, (sections, key) => {
      let stepFields: ReportField[] = [];
      forEach(sections, (value) => {
        const sectionFields = _.filter(value, { checked: true }).map((val) => ({
          id: val.field.id,
        }));
        stepFields = [...stepFields, ...sectionFields];
      });
      reportSteps.push({
        id: key.toString(),
        fields: stepFields,
      });
    });

    createGeneralFieldsObject(generalStep);

    const content: ReportContent = { name: reportName, steps: reportSteps, general: generalFields };
    const report: Partial<Report> = { content, formId };
    if (reportId == 'create') {
      await createReport(organizationId, formId, { ...report, id: uuidv4() } as Report).then(() => {
        addSuccessToast({
          message: intl.formatMessage({ id: 'reports.createSuccess' }),
        });
      });
    } else {
      await updateReport(organizationId, formId, { ...report, id: reportId } as Report).then(() => {
        addSuccessToast({
          message: intl.formatMessage({ id: 'reports.saveSuccess' }),
        });
      });
    }
  };

  const handleCollapseSteps = (section) => {
    const clone = open.slice();
    if (clone.includes(section)) {
      clone.splice(open.indexOf(section), 1);
      setOpen(clone);
    } else if (clone.length == 0) {
      setOpen([section]);
    } else {
      clone.push(section);
      setOpen(clone);
    }
  };

  const handleSubmitReport = () => {
    reportRef.current?.values.reportName != null
      ? setReportName(reportRef.current?.values.reportName)
      : setReportName('');
    reportRef.current?.submitForm();
  };

  const handleGenerateReport = async (steps) => {
    const reportSteps: ReportStep[] = [];
    const tempSteps = cloneDeep(steps);
    const { generalStep, ...otherSteps } = tempSteps;

    forEach(otherSteps, (sections, key) => {
      let stepFields: ReportField[] = [];
      forEach(sections, (value) => {
        const sectionFields = _.filter(value, { checked: true }).map((val) => ({
          id: val.field.id,
        }));
        stepFields = [...stepFields, ...sectionFields];
      });
      reportSteps.push({
        id: key.toString(),
        fields: stepFields,
      });
    });

    createGeneralFieldsObject(generalStep);

    const content: ReportContent = { name: reportName, steps: reportSteps, general: generalFields };
    const report: Partial<Report> = { content, formId, culture: intl.locale };

    await generateReport(organizationId, formId, { ...report, id: uuidv4() } as Report);
  };

  const handleSectionName = (step: string, section: string) => {
    if (form) {
      const currentStep = _.find(formSteps, (currentStep) => currentStep.id == step);
      return currentStep
        ? getText(_.find(currentStep.sections, (currentSection) => currentSection.id == section)?.name, intl.locale)
        : noop;
    }
  };

  const createGeneralFieldsObject = (tempSteps: any) => {
    generalFields = [];
    forEach(tempSteps, (tempStep) => {
      _.filter(tempStep, { checked: true }).map((field) => {
        generalFields.push({ id: field.field.id as string });
      });
    });
  };

  const reportSettingsErrorMessages = Yup.object().shape({
    reportName: Yup.string()
      .min(1, intl.formatMessage({ id: 'formSettings.errorMessage.short' }))
      .max(100, intl.formatMessage({ id: 'formSettings.errorMessage.long' }))
      .required(intl.formatMessage({ id: 'formSettings.errorMessage.required' })),
  });

  return (
    <div className="reports">
      <div className="page-content">
        <Container fluid>
          <PageTitle title={intl.formatMessage({ id: 'navbar.settings.reports.title' })}></PageTitle>
          {!isLoading && fields && form && form.content.steps.length > 0 && (
            <React.Fragment>
              <Row className="report-header">
                <div className="report-id-wrapper">
                  <p>#{formId}</p>
                </div>
                <div className="report-name">
                  <Formik
                    validationSchema={reportSettingsErrorMessages}
                    initialValues={{
                      reportName: reportName,
                    }}
                    onSubmit={() => {
                      handleSaveReport(fields);
                    }}
                    innerRef={reportRef}
                  >
                    {({ errors, touched, handleChange, handleBlur, values }) => {
                      return (
                        <Form>
                          <bootstrapForm.Control
                            type="input"
                            name="reportName"
                            placeholder={intl.formatMessage({ id: 'reports.reportName' })}
                            isInvalid={touched.reportName && !!errors.reportName}
                            onChange={(e) => {
                              if (reportSecurity?.update) {
                                handleChange(e);
                                setReportName(e.target.value);
                              }
                            }}
                            onBlur={handleBlur}
                            value={values.reportName}
                          />
                          <bootstrapForm.Control.Feedback type="invalid">
                            {errors.reportName && touched.reportName && (
                              <FormattedMessage id="reports.emptyReportName"></FormattedMessage>
                            )}
                          </bootstrapForm.Control.Feedback>
                        </Form>
                      );
                    }}
                  </Formik>
                </div>
                <div className="check-button-wrapper">
                  <Button className="m-1" onClick={() => handleCheck(true)} disabled={!reportSecurity?.update}>
                    <span className="mdi mdi-checkbox-marked"></span>
                    {intl.formatMessage({ id: 'reports.checkAll' })}
                  </Button>
                  <Button className="m-1" onClick={() => handleCheck(false)} disabled={!reportSecurity?.update}>
                    <span className="mdi mdi-checkbox-blank"></span>
                    {intl.formatMessage({ id: 'reports.uncheckAll' })}
                  </Button>
                </div>
              </Row>
              <div className="steps">
                {Object.keys(fields).map((step) => {
                  return (
                    <React.Fragment key={step}>
                      <div className="steps-fields">
                        <div className="step-name">
                          {getText(_.find(formSteps, (currentStep) => currentStep.id == step)?.name, intl.locale)}
                        </div>
                        {Object.keys(fields[step]).map((section) => {
                          return (
                            <React.Fragment key={section}>
                              <div id={section}>
                                <Button
                                  variant="outline-primary"
                                  onClick={() => handleCollapseSteps(section)}
                                  aria-expanded={open.includes(section)}
                                >
                                  {open.includes(section) && <span className="mdi mdi-triangle mdi-rotate-180"></span>}
                                  {!open.includes(section) && <span className="mdi mdi-triangle mdi-rotate-90"></span>}
                                  <div className="section-name">{handleSectionName(step, section)}</div>
                                </Button>
                                <Collapse in={open.includes(section)}>
                                  <div className="section">
                                    {_.map(fields[step][section], (field: FieldReport, idx: number) => {
                                      return (
                                        <div className="report-field" key={field.field.id}>
                                          <div className="field-check">
                                            <CheckboxInput
                                              id={field.field.id}
                                              value={field.checked}
                                              onChange={() => handleCheckField(field, step, section)}
                                              enabled={reportSecurity?.update}
                                            />
                                            <p className="m-1">
                                              {checkTranslation(getText(field.field.text, intl.locale), intl)}
                                            </p>
                                          </div>
                                          <Button className="p-1 me-1" disabled={!reportSecurity?.update}>
                                            <span
                                              className="mdi mdi-arrow-up"
                                              onClick={() => handleGoUp(step, idx, section)}
                                            />
                                          </Button>
                                          <Button className="p-1 me-1 down" disabled={!reportSecurity?.update}>
                                            <span
                                              className="mdi mdi-arrow-down"
                                              onClick={() => handleGoDown(step, idx, section)}
                                            />
                                          </Button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </Collapse>
                              </div>
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </React.Fragment>
          )}
          {isLoading && (
            <Col xl={12} className="d-flex justify-content-center loading-overlay">
              <LoadingSpinner />
            </Col>
          )}
          {form && form.content.steps.length == 0 ? (
            <p className="no-fields-report">*{intl.formatMessage({ id: 'reports.noFields' })}</p>
          ) : (
            <Col xl={8}>
              <div className="text-center">
                <Button
                  className="m-1 generate"
                  variant="primary"
                  onClick={() => handleGenerateReport(fields)}
                  disabled={!reportSecurity?.read}
                >
                  {intl.formatMessage({ id: 'reports.generate' })}
                </Button>
                <Button
                  className="m-1 save"
                  variant="primary"
                  onClick={() => handleSubmitReport()}
                  disabled={!reportSecurity?.update}
                >
                  {intl.formatMessage({ id: 'reports.save' })}
                </Button>
              </div>
            </Col>
          )}
        </Container>
      </div>
    </div>
  );
};

export default ReportsPage;
